/**
 * OpsVehicleMappingPanel
 *
 * Optional section within Postcode Stop Assignment that lets users:
 * 1. Authenticate against UAT or Production Ops API.
 * 2. Load available vehicles for a selected network.
 * 3. Define vehicle slots (route/shift intent) and map each to a real Ops vehicle.
 *
 * ELC DAY/LATE/NIGHT is surfaced as a preset that pre-fills slots; generic networks
 * can add slots manually.
 *
 * W9 reads `VehicleSlot.opsVehicleId` from the exported slot list when building
 * route upload shift assignment payloads.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import {
  fetchHubs,
  fetchNetworks,
  type Environment,
  type UATHub,
  type UATNetwork,
} from '../../services/fixedRouteUatClient';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import {
  ENVIRONMENT_OPTIONS,
  getEnvironmentDisplayName,
  getEnvironmentTokenStorageKey,
} from '../../services/environmentConfig';
import { fetchOpsVehiclesForNetwork } from '../../services/postcodeAssignment/opsVehicleAdapter';
import type { VehicleSlot, OpsVehicle, ElcPresetConfig } from '../../services/postcodeAssignment/opsVehicleTypes';

import { Icon } from '@liftango/ui-icons';
import { opsColors } from '../../theme/opsTokens';

// ─── ELC preset defaults ─────────────────────────────────────────────────────

const ELC_PRESET_DEFAULTS: ElcPresetConfig[] = [
  { band: 'DAY', count: 1, startWindow: '03:00', endWindow: '11:00' },
  { band: 'LATE', count: 1, startWindow: '11:00', endWindow: '19:00' },
  { band: 'NIGHT', count: 1, startWindow: '19:00', endWindow: '03:00' },
];

// Whitman Labs depot — pre-populated so users don't have to type the coords
// every run. `activeDepot` recognises this exact pair and labels it accordingly.
const DEFAULT_DEPOT_INPUT = '50.92085541038769, -1.0176777712049292';
const DEFAULT_DEPOT_LATITUDE = 50.92085541038769;
const DEFAULT_DEPOT_LONGITUDE = -1.0176777712049292;
const DEFAULT_DEPOT_LABEL = 'Whitman Labs';

// Depot snapshot used when stamping fresh slots. `null` means no depot has been
// chosen yet — the propagation effect will fill the fields in when one is
// selected. Stamping at build-time (rather than relying solely on the effect)
// guarantees slots created via ELC preset / Add slot AFTER the depot was set
// still carry the coords; the effect only re-runs when `activeDepot` itself
// changes.
type DepotStamp = { latitude: number; longitude: number; label: string } | null;

function applyDepotStamp<T extends VehicleSlot>(slot: T, depot: DepotStamp): T {
  if (!depot) return slot;
  return {
    ...slot,
    depotLatitude: depot.latitude,
    depotLongitude: depot.longitude,
    depotLabel: depot.label,
  };
}

function buildElcSlots(configs: ElcPresetConfig[], depot: DepotStamp): VehicleSlot[] {
  const slots: VehicleSlot[] = [];
  for (const cfg of configs) {
    for (let i = 1; i <= cfg.count; i += 1) {
      slots.push(applyDepotStamp({
        localId: `${cfg.band}-${i}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: `${cfg.band} ${i} COACH`,
        startWindow: cfg.startWindow,
        endWindow: cfg.endWindow,
        opsVehicleId: '',
        opsVehicleLabel: '',
      }, depot));
    }
  }
  return slots;
}

function buildEmptySlot(depot: DepotStamp): VehicleSlot {
  return applyDepotStamp({
    localId: `slot-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: '',
    startWindow: '',
    endWindow: '',
    opsVehicleId: '',
    opsVehicleLabel: '',
  }, depot);
}

// ─── Validation helpers ───────────────────────────────────────────────────────

function findDuplicateOpsVehicleIds(slots: VehicleSlot[]): Set<string> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const slot of slots) {
    if (!slot.opsVehicleId) continue;
    if (seen.has(slot.opsVehicleId)) {
      dupes.add(slot.opsVehicleId);
    } else {
      seen.add(slot.opsVehicleId);
    }
  }
  return dupes;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface OpsVehicleMappingPanelProps {
  /** Current vehicle slots managed by the parent page. */
  vehicleSlots: VehicleSlot[];
  /** Called whenever slot data changes; parent stores and exports this. */
  onVehicleSlotsChange: (slots: VehicleSlot[]) => void;
  /** Shift band chosen in parent page; drives default slot windows. */
  selectedShiftBand: ElcPresetConfig['band'];
}

// ─── Component ────────────────────────────────────────────────────────────────

export const OpsVehicleMappingPanel: React.FC<OpsVehicleMappingPanelProps> = ({
  vehicleSlots,
  onVehicleSlotsChange,
  selectedShiftBand,
}) => {
  // Auth
  const [jwtToken, setJwtToken] = React.useState('');
  const [showJwt, setShowJwt] = React.useState(false);
  const [environment, setEnvironment] = React.useState<Environment>('UAT');

  const jwtStorageKey = getEnvironmentTokenStorageKey(environment);

  // Hydrate JWT from localStorage when the environment key changes (mount or env switch).
  // Intentionally excludes `jwtToken` from deps: including it caused the effect to re-run
  // on every keystroke, which silently reverted user edits back to the saved value.
  React.useEffect(() => {
    const savedToken = localStorage.getItem(jwtStorageKey) || '';
    if (savedToken) {
      setJwtToken(savedToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtStorageKey]);

  function handleJwtTokenChange(value: string) {
    setJwtToken(value);
    const trimmedToken = value.trim();
    if (trimmedToken) {
      localStorage.setItem(jwtStorageKey, trimmedToken);
    } else {
      localStorage.removeItem(jwtStorageKey);
    }
  }

  function handleEnvironmentChange(nextEnvironment: Environment) {
    const nextToken = localStorage.getItem(getEnvironmentTokenStorageKey(nextEnvironment)) || '';
    setEnvironment(nextEnvironment);
    setJwtToken(nextToken);
    setNetworks([]);
    setSelectedProductId('');
    setSelectedNetworkId('');
    setOpsVehicles([]);
    setNetworkError(null);
    setVehicleLoadWarning(null);
    setVehicleLoadError(null);
    setHubs([]);
    setSelectedHubId('');
    setManualDepotInput(DEFAULT_DEPOT_INPUT);
    setHubsError(null);
  }

  // Network selection
  const [networks, setNetworks] = React.useState<UATNetwork[]>([]);
  const [networksLoading, setNetworksLoading] = React.useState(false);
  const [networkError, setNetworkError] = React.useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = React.useState('');
  const [selectedNetworkId, setSelectedNetworkId] = React.useState('');

  // Vehicle loading
  const [opsVehicles, setOpsVehicles] = React.useState<OpsVehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = React.useState(false);
  const [vehicleLoadWarning, setVehicleLoadWarning] = React.useState<string | null>(null);
  const [vehicleLoadError, setVehicleLoadError] = React.useState<string | null>(null);

  // Depot (vehicle start/end). Hubs are fetched from Ops getservicesettings;
  // users can also enter "lat, lng" manually. `selectedHubId === ''` means manual mode.
  const [hubs, setHubs] = React.useState<UATHub[]>([]);
  const [hubsLoading, setHubsLoading] = React.useState(false);
  const [hubsError, setHubsError] = React.useState<string | null>(null);
  const [selectedHubId, setSelectedHubId] = React.useState<string>('');
  const [manualDepotInput, setManualDepotInput] = React.useState<string>(DEFAULT_DEPOT_INPUT);

  // ELC preset counts
  const [elcCounts, setElcCounts] = React.useState<Record<string, number>>({
    DAY: 1,
    LATE: 1,
    NIGHT: 1,
  });
  const autoLoadedNetworkKeysRef = React.useRef<Set<string>>(new Set());
  const selectedNetwork = networks.find((n) => n.productId === selectedProductId);
  const selectedShiftConfig = ELC_PRESET_DEFAULTS.find((cfg) => cfg.band === selectedShiftBand) ?? ELC_PRESET_DEFAULTS[0];

  // Resolve the current depot coordinates from either a selected hub or manual entry.
  // Returns null when no valid depot has been chosen — callers leave coords undefined.
  const activeDepot = React.useMemo<{ latitude: number; longitude: number; label: string } | null>(() => {
    if (selectedHubId) {
      const hub = hubs.find((h) => h.addressId === selectedHubId);
      if (hub && typeof hub.latitude === 'number' && typeof hub.longitude === 'number') {
        return { latitude: hub.latitude, longitude: hub.longitude, label: hub.name || hub.label || 'Depot' };
      }
      return null;
    }
    const trimmed = manualDepotInput.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(',').map((s) => s.trim());
    if (parts.length !== 2) return null;
    const lat = Number(parts[0]);
    const lng = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    // Match Whitman Labs by parsed numeric equality so a user who edits and
    // re-types the same coords still gets the friendly label.
    if (lat === DEFAULT_DEPOT_LATITUDE && lng === DEFAULT_DEPOT_LONGITUDE) {
      return { latitude: lat, longitude: lng, label: DEFAULT_DEPOT_LABEL };
    }
    return { latitude: lat, longitude: lng, label: `Manual (${lat.toFixed(4)}, ${lng.toFixed(4)})` };
  }, [selectedHubId, hubs, manualDepotInput]);

  // ── Load networks ────────────────────────────────────────────────────────────
  async function handleLoadNetworks() {
    if (!jwtToken.trim()) return;
    setNetworksLoading(true);
    setNetworkError(null);
    setNetworks([]);
    setSelectedProductId('');
    setSelectedNetworkId('');
    setOpsVehicles([]);
    setVehicleLoadWarning(null);
    try {
      const response = await fetchNetworks(jwtToken, environment);
      const list: UATNetwork[] = (response?.data?.entry ?? []).slice().sort((a, b) =>
        a.productName.localeCompare(b.productName, undefined, { sensitivity: 'base' }),
      );
      setNetworks(list);
      if (list.length === 0) {
        setNetworkError('No networks returned. Check your JWT token and environment.');
      }
    } catch (err) {
      setNetworkError(err instanceof Error ? err.message : 'Failed to load networks');
    } finally {
      setNetworksLoading(false);
    }
  }

  // Auto-load networks when a saved token is already present.
  React.useEffect(() => {
    const token = jwtToken.trim();
    if (!token || networksLoading) return;
    const key = `${environment}:${token}`;
    if (autoLoadedNetworkKeysRef.current.has(key)) return;
    autoLoadedNetworkKeysRef.current.add(key);
    void handleLoadNetworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, environment, networksLoading, networks.length, networkError]);

  // Fetch hubs (depot candidates) whenever the selected product changes.
  // Failures here are non-fatal — users can still type a manual lat,lng below.
  React.useEffect(() => {
    if (!selectedProductId || !jwtToken.trim()) {
      setHubs([]);
      setHubsError(null);
      return;
    }
    let cancelled = false;
    setHubsLoading(true);
    setHubsError(null);
    fetchHubs(jwtToken, selectedProductId, DEFAULT_SIMULATION_DATE, DEFAULT_SIMULATION_DATE, environment)
      .then((result) => {
        if (cancelled) return;
        setHubs(Array.isArray(result) ? result : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setHubs([]);
        setHubsError(err instanceof Error ? err.message : 'Failed to load hubs');
      })
      .finally(() => {
        if (!cancelled) setHubsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, environment]);

  // When the depot changes, stamp it onto every existing slot so push-to-map
  // emits non-zero start/end addresses for all vehicles.
  React.useEffect(() => {
    if (vehicleSlots.length === 0) return;
    const needsUpdate = vehicleSlots.some(
      (s) =>
        s.depotLatitude !== activeDepot?.latitude ||
        s.depotLongitude !== activeDepot?.longitude ||
        s.depotLabel !== activeDepot?.label,
    );
    if (!needsUpdate) return;
    onVehicleSlotsChange(
      vehicleSlots.map((s) => ({
        ...s,
        depotLatitude: activeDepot?.latitude,
        depotLongitude: activeDepot?.longitude,
        depotLabel: activeDepot?.label,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDepot]);

  // ── Load vehicles ────────────────────────────────────────────────────────────
  async function handleLoadVehicles() {
    if (!jwtToken.trim() || !selectedProductId) return;
    const net = networks.find((n) => n.productId === selectedProductId);
    if (!net) return;
    setVehiclesLoading(true);
    setVehicleLoadError(null);
    setVehicleLoadWarning(null);
    setOpsVehicles([]);
    try {
      const result = await fetchOpsVehiclesForNetwork(
        jwtToken,
        selectedProductId,
        net.networkUuid ?? '',
        environment,
        net.productName
      );
      setOpsVehicles(result.vehicles);
      onVehicleSlotsChange(
        result.vehicles.map((vehicle, index) => ({
          localId: `auto-${vehicle.id}-${Date.now()}-${index}`,
          name: vehicle.rego || vehicle.label || `Vehicle ${index + 1}`,
          startWindow: selectedShiftConfig.startWindow,
          endWindow: selectedShiftConfig.endWindow,
          opsVehicleId: vehicle.id,
          opsVehicleLabel:
            `${vehicle.rego || vehicle.label || vehicle.id}${vehicle.seatingCapacity ? ` (${vehicle.seatingCapacity})` : ''}`,
          depotLatitude: activeDepot?.latitude,
          depotLongitude: activeDepot?.longitude,
          depotLabel: activeDepot?.label,
        }))
      );
      if (result.warning) setVehicleLoadWarning(result.warning);
    } catch (err) {
      setVehicleLoadError(err instanceof Error ? err.message : 'Failed to load vehicles');
    } finally {
      setVehiclesLoading(false);
    }
  }

  // ── Slot helpers ─────────────────────────────────────────────────────────────
  function updateSlot(localId: string, patch: Partial<VehicleSlot>) {
    onVehicleSlotsChange(
      vehicleSlots.map((s) => {
        if (s.localId !== localId) return s;
        const merged = { ...s, ...patch };
        if (patch.opsVehicleId !== undefined) {
          const vehicle = opsVehicles.find((v) => v.id === patch.opsVehicleId);
          merged.opsVehicleLabel = vehicle?.label ?? '';
        }
        return merged;
      })
    );
  }

  function removeSlot(localId: string) {
    onVehicleSlotsChange(vehicleSlots.filter((s) => s.localId !== localId));
  }

  function addEmptySlot() {
    onVehicleSlotsChange([...vehicleSlots, buildEmptySlot(activeDepot)]);
  }

  function applyElcPreset() {
    const configs: ElcPresetConfig[] = ELC_PRESET_DEFAULTS.map((d) => ({
      ...d,
      count: elcCounts[d.band] ?? 1,
    }));
    onVehicleSlotsChange(buildElcSlots(configs, activeDepot));
  }

  // ── Derived validation ────────────────────────────────────────────────────────
  const duplicateOpsIds = findDuplicateOpsVehicleIds(vehicleSlots);
  const unmappedSlotCount = vehicleSlots.filter((s) => !s.opsVehicleId).length;
  const hasVehicleSource = opsVehicles.length > 0;

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Stack
      spacing={2.5}
      data-testid="transfer.postcode-panel.root"
      data-mapped-count={vehicleSlots.filter((s) => s.opsVehicleId).length}
      data-unmapped-count={unmappedSlotCount}
    >
      {/* Credentials & environment */}
      <Stack spacing={1.5}>
        <Typography
          variant="subtitle2"
          component="div"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <Icon name="bus" size={20} aria-hidden />
          Ops API credentials
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
          <TextField
            label="JWT token"
            type={showJwt ? 'text' : 'password'}
            value={jwtToken}
            onChange={(e) => handleJwtTokenChange(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowJwt((v) => !v)}
                    aria-label={showJwt ? 'Hide token' : 'Show token'}
                  >
                    {showJwt ? (
                      <Icon name="eye-slash" size={20} />
                    ) : (
                      <Icon name="eye" size={20} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="ops-env-label">Environment</InputLabel>
            <Select
              labelId="ops-env-label"
              value={environment}
              label="Environment"
              onChange={(e) => handleEnvironmentChange(e.target.value as Environment)}
            >
              {ENVIRONMENT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            disabled={!jwtToken.trim() || networksLoading}
            onClick={() => void handleLoadNetworks()}
            startIcon={networksLoading ? <CircularProgress size={14} color="inherit" /> : <Icon name="file-arrow-down" size={20} />}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Load networks
          </Button>
        </Stack>
        {!jwtToken.trim() && (
          <Alert severity="info" icon={<Icon name="triangle-exclamation" size={20} />}>
            No saved {getEnvironmentDisplayName(environment)} Ops token found. Paste a JWT above, or save one in your account/profile so it can be reused here.
          </Alert>
        )}
      </Stack>

      {/* Network / product selector */}
      {networks.length > 0 && (
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">Network / product</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="ops-product-label">Product / network</InputLabel>
              <Select
                labelId="ops-product-label"
                label="Product / network"
                value={selectedProductId}
                onChange={(e) => {
                  setSelectedProductId(e.target.value);
                  const net = networks.find((n) => n.productId === e.target.value);
                  setSelectedNetworkId(net?.networkUuid ?? String(net?.defaultNetworkId ?? ''));
                  setOpsVehicles([]);
                  setVehicleLoadError(null);
                  setVehicleLoadWarning(null);
                }}
              >
                {networks.map((n) => (
                  <MenuItem key={n.productId} value={n.productId}>
                    {n.productName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              disabled={!selectedProductId || !selectedNetworkId || vehiclesLoading}
              onClick={() => void handleLoadVehicles()}
              startIcon={vehiclesLoading ? <CircularProgress size={14} color="inherit" /> : <Icon name="file-arrow-down" size={20} />}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'flex-start', mt: { xs: 0, sm: 0 } }}
            >
              Load vehicles
            </Button>
          </Stack>
          {selectedNetwork && (
            <Typography variant="caption" color="text.secondary">
              {selectedNetwork.networkUuid
                ? `Network UUID: ${selectedNetwork.networkUuid}`
                : `Network dbId: ${selectedNetwork.defaultNetworkId} (UUID will be resolved when loading vehicles)`}
              {selectedNetwork.timezone ? ` · ${selectedNetwork.timezone}` : ''}
            </Typography>
          )}
        </Stack>
      )}

      {/* Depot (vehicle start/end location) */}
      {selectedProductId && (
        <Stack spacing={1.25}>
          <Typography
            variant="subtitle2"
            component="div"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            <Icon name="location-dot" size={20} aria-hidden />
            Depot (vehicle start &amp; end)
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} disabled={hubsLoading || hubs.length === 0}>
              <InputLabel id="ops-hub-label">
                {hubsLoading ? 'Loading hubs…' : hubs.length === 0 ? 'No hubs available' : 'Hub'}
              </InputLabel>
              <Select
                labelId="ops-hub-label"
                label={hubsLoading ? 'Loading hubs…' : hubs.length === 0 ? 'No hubs available' : 'Hub'}
                value={selectedHubId}
                onChange={(e) => {
                  setSelectedHubId(e.target.value);
                  if (e.target.value) setManualDepotInput('');
                }}
              >
                <MenuItem value="">
                  <em>Manual entry</em>
                </MenuItem>
                {hubs.map((hub) => (
                  <MenuItem key={hub.addressId} value={hub.addressId}>
                    {hub.name || hub.label || hub.addressId}
                    {typeof hub.latitude === 'number' && typeof hub.longitude === 'number'
                      ? ` (${hub.latitude.toFixed(4)}, ${hub.longitude.toFixed(4)})`
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Manual lat, lng"
              placeholder="e.g. 51.0067, -0.9519"
              value={manualDepotInput}
              onChange={(e) => {
                setManualDepotInput(e.target.value);
                if (e.target.value.trim()) setSelectedHubId('');
              }}
              disabled={!!selectedHubId}
              sx={{ flex: 1, minWidth: 0 }}
              inputProps={{ 'aria-label': 'Manual depot lat,lng' }}
            />
          </Stack>
          {hubsError && (
            <Alert severity="warning" sx={{ py: 0.5 }} icon={<Icon size={20} name="triangle-exclamation" />}>
              Could not load hubs from Ops ({hubsError}). Enter depot coordinates manually below.
            </Alert>
          )}
          {activeDepot ? (
            <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon size={20} name="location-dot" /> Depot set: {activeDepot.label}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Choose a hub or enter "lat, lng" — applied to every vehicle's start and end location on push.
            </Typography>
          )}
        </Stack>
      )}

      {/* Errors & warnings */}
      {networkError && <Alert severity="error" sx={{ py: 0.5 }}>{networkError}</Alert>}
      {vehicleLoadError && (
        <Alert severity="error" sx={{ py: 0.5, '& .MuiAlert-message': { whiteSpace: 'pre-wrap' } }}>
          {vehicleLoadError}
        </Alert>
      )}
      {vehicleLoadWarning && (
        <Alert severity="info" icon={<Icon size={20} name="triangle-exclamation" />} sx={{ py: 0.5 }}>
          {vehicleLoadWarning}
        </Alert>
      )}
      {opsVehicles.length > 0 && (
        <Alert severity="success" sx={{ py: 0.5 }}>
          Loaded {opsVehicles.length} vehicle{opsVehicles.length === 1 ? '' : 's'} from Ops.
          {vehicleSlots.length === 0 ? ' Add a slot (or apply ELC preset) to map them.' : ''}
        </Alert>
      )}

      <Divider />

      {/* Vehicle slot management */}
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Vehicle slots
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={addEmptySlot}
            startIcon={<Icon name="plus" size={20} />}
          >
            Add slot
          </Button>
          <Tooltip
            title="Pre-fill with ELC Day / Late / Night shift bands"
            enterTouchDelay={0}
            placement="top"
          >
            <Button
              variant="outlined"
              size="small"
              onClick={applyElcPreset}
              color="secondary"
            >
              ELC preset
            </Button>
          </Tooltip>
        </Stack>

        {/* ELC preset count configurator */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          useFlexGap
          sx={{
            p: 1.25,
            borderRadius: 1,
            bgcolor: alpha(opsColors.neutral.panel, 0.5),
            border: `1px solid ${opsColors.neutral.line}`,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ pt: 0.75, flexShrink: 0 }}>
            ELC preset counts:
          </Typography>
          {(['DAY', 'LATE', 'NIGHT'] as const).map((band) => (
            <TextField
              key={band}
              label={band}
              type="number"
              size="small"
              value={elcCounts[band] ?? 1}
              onChange={(e) => {
                const n = Math.max(1, Math.round(Number(e.target.value) || 1));
                setElcCounts((prev) => ({ ...prev, [band]: n }));
              }}
              inputProps={{ min: 1, max: 20, step: 1 }}
              sx={{ width: 80 }}
            />
          ))}
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Mapping Ops vehicles enables strict vehicle assignment during route upload. Unmapped
          slots can still upload with a warning.
        </Typography>

        {/* Validation warnings */}
        {vehicleSlots.length > 0 && !hasVehicleSource && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            No Ops vehicles loaded. Load vehicles above to map slots, or leave slots unmapped
            (W9 will upload without vehicle assignment).
          </Alert>
        )}
        {duplicateOpsIds.size > 0 && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {duplicateOpsIds.size} Ops vehicle
            {duplicateOpsIds.size === 1 ? ' is' : 's are'} assigned to more than one slot.
            Duplicate assignments will likely fail during route upload.
          </Alert>
        )}
        {vehicleSlots.length > 0 && unmappedSlotCount > 0 && hasVehicleSource && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            {unmappedSlotCount} slot{unmappedSlotCount === 1 ? '' : 's'} not yet mapped to an
            Ops vehicle. Unmapped slots will upload without vehicle assignment.
          </Alert>
        )}

        {/* Slots table */}
        {vehicleSlots.length > 0 ? (
          <TableContainer
            data-testid="transfer.postcode-panel.vehicle-list"
            sx={{
              border: `1px solid ${opsColors.neutral.line}`,
              borderRadius: 1,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(opsColors.neutral.panel, 0.6) }}>
                  <TableCell sx={{ minWidth: 160 }}>Route / vehicle name</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Start window</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>End window</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Ops vehicle</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicleSlots.map((slot) => {
                  const isDupe = duplicateOpsIds.has(slot.opsVehicleId);
                  return (
                    <TableRow
                      key={slot.localId}
                      sx={{
                        '&:last-child td': { border: 0 },
                        ...(isDupe
                          ? { bgcolor: alpha(opsColors.semantic.warn.color, 0.07) }
                          : {}),
                      }}
                    >
                      <TableCell>
                        <TextField
                          size="small"
                          variant="standard"
                          value={slot.name}
                          onChange={(e) => updateSlot(slot.localId, { name: e.target.value })}
                          placeholder="e.g. DAY 1 COACH"
                          fullWidth
                          inputProps={{ 'aria-label': 'Vehicle slot name' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          variant="standard"
                          value={slot.startWindow}
                          onChange={(e) => updateSlot(slot.localId, { startWindow: e.target.value })}
                          placeholder="HH:MM"
                          inputProps={{ 'aria-label': 'Shift start window' }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          variant="standard"
                          value={slot.endWindow}
                          onChange={(e) => updateSlot(slot.localId, { endWindow: e.target.value })}
                          placeholder="HH:MM"
                          inputProps={{ 'aria-label': 'Shift end window' }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        {hasVehicleSource ? (
                          <FormControl variant="standard" size="small" fullWidth>
                            <Select
                              value={slot.opsVehicleId}
                              onChange={(e) =>
                                updateSlot(slot.localId, { opsVehicleId: e.target.value })
                              }
                              displayEmpty
                              renderValue={(val) =>
                                val ? (
                                  <Chip
                                    label={
                                      (() => {
                                        const selected = opsVehicles.find((v) => v.id === val);
                                        if (!selected) return val;
                                        const base = selected.rego || selected.label || selected.id;
                                        return selected.seatingCapacity ? `${base} · ${selected.seatingCapacity}` : base;
                                      })()
                                    }
                                    size="small"
                                    color={isDupe ? 'warning' : 'default'}
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.disabled">
                                    — not mapped —
                                  </Typography>
                                )
                              }
                              inputProps={{ 'aria-label': 'Ops vehicle assignment' }}
                            >
                              <MenuItem value="">
                                <em>Not mapped</em>
                              </MenuItem>
                              {opsVehicles.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                  {(v.rego || v.label || v.id)}
                                  {v.seatingCapacity ? ` · ${v.seatingCapacity}` : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            {slot.opsVehicleId
                              ? slot.opsVehicleLabel || slot.opsVehicleId
                              : '— load vehicles above —'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => removeSlot(slot.localId)}
                          aria-label="Remove slot"
                          sx={{ color: 'text.secondary' }}
                        >
                          <Icon name="trash" size={20} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              py: 2.5,
              textAlign: 'center',
              border: `1px dashed ${opsColors.neutral.line}`,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No slots defined. Click <strong>Add slot</strong> or use the{' '}
              <strong>ELC preset</strong> to get started.
            </Typography>
          </Box>
        )}

        {/* Summary chips */}
        {vehicleSlots.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`${vehicleSlots.length} slot${vehicleSlots.length === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${vehicleSlots.filter((s) => s.opsVehicleId).length} mapped`}
              size="small"
              color={
                vehicleSlots.filter((s) => s.opsVehicleId).length === vehicleSlots.length
                  ? 'success'
                  : 'default'
              }
              variant="outlined"
            />
            {duplicateOpsIds.size > 0 && (
              <Chip
                label={`${duplicateOpsIds.size} duplicate assignment${duplicateOpsIds.size > 1 ? 's' : ''}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
